"use client";

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import BriefingSection from "./BriefingSection";
import BriefingActions from "./BriefingActions";
import { useFocusObserver } from "../hooks/useFocusObserver";
import type { AnswerValue, BriefingConfig, BriefingFormProps } from "../types";

/**
 * The one thing that differs between questionnaires: the live side panel.
 * The shell owns placement (mobile inline cards, desktop sticky rail, the
 * fullscreen modal) and the focus plumbing; the aside renders the cards.
 */
export interface BriefingAside<F extends string> {
  /** Inline card above each section that feeds the panel (below lg the sticky
      rail is gone — without this the client never sees what their answers are
      building). Returns the whole card including its `lg:hidden` classes. */
  renderMobile: (focus: F, openModal: () => void) => ReactNode;
  /** Card at the top of the desktop sticky rail. */
  renderDesktop: (focus: F | undefined, openModal: () => void) => ReactNode;
  /** Fullscreen review modal; its presence enables the expand/modal plumbing. */
  modal?: {
    ariaLabel: string;
    title: ReactNode;
    closeLabel: string;
    /** Card content below the shell-rendered header row (panel + caption). */
    render: () => ReactNode;
  };
  /** Vertical rhythm of the section column (design uses the wider spacing). */
  sectionsGapClass?: string;
}

interface BriefingFormShellProps<F extends string> extends BriefingFormProps<F> {
  config: BriefingConfig;
  aside: BriefingAside<F>;
}

const CHANGE_FOCUS_MS = 2000;

export default function BriefingFormShell<F extends string>({
  data,
  answers,
  onChange,
  onImport,
  onReset,
  hasHydrated,
  sentAt,
  changedSinceSent,
  onMarkSent,
  config,
  aside,
}: BriefingFormShellProps<F>) {
  const scrollFocus = useFocusObserver(data.sections);

  // "Mark what is affected": an answer change immediately emphasises the zone
  // or block it feeds, overriding scroll-driven focus for a moment — without
  // it, a change landing in a dimmed area is easy to miss.
  const [changedFocus, setChangedFocus] = useState<F | undefined>(undefined);
  const changeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setModalOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [modalOpen]);

  const focusByQuestion = useMemo(() => {
    const map: Record<string, F | undefined> = {};
    for (const section of data.sections) {
      for (const question of section.questions) {
        map[question.id] = section.focus;
      }
    }
    return map;
  }, [data.sections]);

  const handleChange = useCallback(
    (questionId: string, value: AnswerValue) => {
      onChange(questionId, value);
      const focus = focusByQuestion[questionId];
      if (focus) {
        setChangedFocus(focus);
        if (changeTimer.current) clearTimeout(changeTimer.current);
        changeTimer.current = setTimeout(() => setChangedFocus(undefined), CHANGE_FOCUS_MS);
      }
    },
    [onChange, focusByQuestion]
  );

  useEffect(() => {
    return () => {
      if (changeTimer.current) clearTimeout(changeTimer.current);
    };
  }, []);

  const desktopFocus = changedFocus ?? scrollFocus;

  const actions = (
    <BriefingActions
      data={data}
      config={config}
      answers={answers}
      onImport={onImport}
      onReset={onReset}
      hasHydrated={hasHydrated}
      sentAt={sentAt}
      changedSinceSent={changedSinceSent}
      onMarkSent={onMarkSent}
    />
  );

  return (
    <div id="briefing" className="lg:grid lg:grid-cols-12 lg:gap-10">
      <div className={`lg:col-span-7 ${aside.sectionsGapClass ?? "space-y-10"}`}>
        {data.sections.map((section) => (
          <div key={section.id}>
            {section.focus ? aside.renderMobile(section.focus, openModal) : null}
            <BriefingSection section={section} answers={answers} onChange={handleChange} />
          </div>
        ))}
      </div>

      <div className="hidden lg:block lg:col-span-5">
        <div className="sticky top-32 space-y-4">
          {aside.renderDesktop(desktopFocus, openModal)}
          {actions}
        </div>
      </div>

      {/* Mobile actions bar — same component, stacked below the form instead of beside it. */}
      <div className="lg:hidden mt-8">{actions}</div>

      {/*
        Fullscreen review modal — rendered via portal into document.body.
        `<main>` has a site-wide `contain: layout style` rule (globals.scss,
        layout-shift protection), which creates a new containing block for any
        `position: fixed` descendant — the modal was fixing to <main>'s box
        instead of the viewport, so it didn't cover the header and didn't stay
        put on scroll. A portal escapes that (and any future transform/filter
        ancestor) instead of fighting the global rule locally.
      */}
      {aside.modal && modalOpen && typeof document !== "undefined"
        ? createPortal(
            <div
              className="no-print fixed inset-0 z-50 flex items-start md:items-center justify-center overflow-y-auto bg-black/70 p-4 md:p-10"
              onClick={closeModal}
              role="dialog"
              aria-modal="true"
              aria-label={aside.modal.ariaLabel}
            >
              {/* max-h + internal scroll: on short viewports (laptops) the panel
                  plus header/caption exceeds the screen — without this the card
                  clips inside the fixed overlay with no way to reach the rest. */}
              <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl p-4 md:p-6 max-h-[92dvh] overflow-y-auto my-auto"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-gray-700">{aside.modal.title}</p>
                  <button
                    type="button"
                    onClick={closeModal}
                    aria-label={aside.modal.closeLabel}
                    className="text-gray-400 hover:text-gray-700 transition-colors"
                  >
                    <X className="w-5 h-5" aria-hidden />
                  </button>
                </div>
                {aside.modal.render()}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
